/**
 * pickNearestPoint — single parameterized point-feature picker.
 *
 * Created as Phase 4 of the HI-2' refactor (audit synthesis 2026-05-24).
 * Replaces the 8 nearly-identical picker bodies in MapView.tsx that each
 * repeated the `queryRenderedFeatures + project + Math.hypot` pattern:
 *   pickPirepAt, pickWxCamAt, pickObstacleAt, pickRepeaterAt,
 *   pickTrafficAt, pickDipSiteAt, pickIhogRingAt, pickUserWaypointAt.
 *
 * NOTE: queryAirspaceAt is NOT replaced here — it's a polygon picker
 * with a different shape (multi-layer, multi-kind, no nearest-point logic).
 *
 * NOTE: pickIhogRingAt is also NOT fully replaced here — it queries a fill
 * layer (not a point layer) and has ring-priority logic (prefer exclusion
 * over contact). It stays as its own function in MapView.
 */

import type { Map as MlMap, MapGeoJSONFeature } from 'maplibre-gl';

/**
 * Query for the nearest rendered point feature in the given layer(s) at
 * the given screen point. Returns the closest feature within `radiusPx`
 * pixels, or null if nothing is in range.
 *
 * @param map       Live MapLibre map instance
 * @param layerIds  One or more layer ids to query (first present layer wins
 *                  if multiple are given, but all are queried in one call)
 * @param point     Screen-space tap point { x, y }
 * @param radiusPx  Half-side of the query box (fingertip-sized ≈ 18 px)
 */
export function pickNearestPoint(
  map: MlMap,
  layerIds: string | ReadonlyArray<string>,
  point: { x: number; y: number },
  radiusPx = 18,
): MapGeoJSONFeature | null {
  const ids = Array.isArray(layerIds) ? layerIds as string[] : [layerIds as string];

  // Only query layers that actually exist on the map right now.
  const present = ids.filter((id) => !!map.getLayer(id));
  if (present.length === 0) return null;

  const r = Math.max(4, radiusPx);
  const bbox: [[number, number], [number, number]] = [
    [point.x - r, point.y - r],
    [point.x + r, point.y + r],
  ];

  const features = map.queryRenderedFeatures(bbox, { layers: present });
  if (features.length === 0) return null;

  // Pick the feature whose geometry-projected screen position is nearest
  // to the tap point. Non-Point geometries are skipped (polygons etc.).
  let bestDist = Infinity;
  let best: MapGeoJSONFeature | null = null;
  for (const f of features) {
    const geom = f.geometry as GeoJSON.Geometry;
    if (!geom || geom.type !== 'Point') continue;
    const [lon, lat] = (geom as GeoJSON.Point).coordinates as [number, number];
    const projected = map.project([lon, lat]);
    const d = Math.hypot(projected.x - point.x, projected.y - point.y);
    if (d < bestDist) {
      bestDist = d;
      best = f;
    }
  }
  return best;
}

/**
 * Convenience: pickNearestPoint then extract geometry lat/lon.
 * Returns null if nothing was found or the geometry isn't a Point.
 */
export function pickNearestPointWithLatLon(
  map: MlMap,
  layerIds: string | ReadonlyArray<string>,
  point: { x: number; y: number },
  radiusPx = 18,
): (Record<string, unknown> & { lat: number; lon: number }) | null {
  const f = pickNearestPoint(map, layerIds, point, radiusPx);
  if (!f) return null;
  const geom = f.geometry as GeoJSON.Geometry;
  if (!geom || geom.type !== 'Point') return null;
  const [lon, lat] = (geom as GeoJSON.Point).coordinates as [number, number];
  return { ...(f.properties as Record<string, unknown> ?? {}), lat, lon };
}
